import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { LanguageProvider, useLanguage } from "@/lib/i18n/LanguageContext";
import Index from "./pages/Index";
import type { Language } from "./lib/i18n/translations";

// Lazy-load non-critical pages for smaller initial bundle
const CreateParty = lazy(() => import("./pages/CreateParty"));
const GuestView = lazy(() => import("./pages/GuestView"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ThankYouCard = lazy(() => import("./pages/ThankYouCard"));
const Auth = lazy(() => import("./pages/Auth"));
const MyParties = lazy(() => import("./pages/MyParties"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PolicyPage = lazy(() => import("./pages/PolicyPage"));
const PlayCenterPage = lazy(() => import("./pages/PlayCenterPage"));

const queryClient = new QueryClient();

/** Syncs the :lang URL param with LanguageContext */
function LanguageRouteWrapper({ children }: { children: React.ReactNode }) {
  const { lang } = useParams<{ lang: string }>();
  const { setLanguage, language } = useLanguage();

  useEffect(() => {
    if (lang === "sv" || lang === "en") {
      if (lang !== language) {
        setLanguage(lang as Language);
      }
    }
  }, [lang, language, setLanguage]);

  if (lang !== "sv" && lang !== "en") {
    return <Navigate to="/sv/" replace />;
  }

  return <>{children}</>;
}

const App = () => {
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Navigate to="/sv/" replace />} />
              
              <Route path="/:lang" element={<LanguageRouteWrapper><Index /></LanguageRouteWrapper>} />
              <Route path="/:lang/auth" element={<LanguageRouteWrapper><Auth /></LanguageRouteWrapper>} />
              <Route path="/:lang/my-parties" element={<LanguageRouteWrapper><MyParties /></LanguageRouteWrapper>} />
              <Route path="/:lang/create" element={<LanguageRouteWrapper><CreateParty /></LanguageRouteWrapper>} />
              <Route path="/:lang/party/:partyId" element={<LanguageRouteWrapper><GuestView /></LanguageRouteWrapper>} />
              <Route path="/:lang/admin/:adminSecret" element={<LanguageRouteWrapper><AdminDashboard /></LanguageRouteWrapper>} />
              <Route path="/:lang/thanks/:partyId" element={<LanguageRouteWrapper><ThankYouCard /></LanguageRouteWrapper>} />
              <Route path="/:lang/policy" element={<LanguageRouteWrapper><PolicyPage /></LanguageRouteWrapper>} />
              <Route path="/:lang/lekland/:centerId" element={<LanguageRouteWrapper><PlayCenterPage /></LanguageRouteWrapper>} />

              {/* Legacy routes */}
              <Route path="/auth" element={<Navigate to="/sv/auth" replace />} />
              <Route path="/my-parties" element={<Navigate to="/sv/my-parties" replace />} />
              <Route path="/create" element={<Navigate to="/sv/create" replace />} />
              <Route path="/party/:partyId" element={<RedirectWithParam prefix="/sv/party" param="partyId" />} />
              <Route path="/admin/:adminSecret" element={<RedirectWithParam prefix="/sv/admin" param="adminSecret" />} />
              <Route path="/thanks/:partyId" element={<RedirectWithParam prefix="/sv/thanks" param="partyId" />} />
              <Route path="/policy" element={<Navigate to="/sv/policy" replace />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
  );
};

function RedirectWithParam({ prefix, param }: { prefix: string; param: string }) {
  const params = useParams();
  return <Navigate to={`${prefix}/${params[param]}`} replace />;
}

export default App;
